import {
    IEditorCanvas, IEditorElementCollection, IEditorPage,
    IEditorRenderer,
    IEditorUtil,
    Imports,
    IStorefrontStaticCanvas
} from "./editor.interfaces";
import {fabric} from "fabric";
import {EditorHelpers} from "@/editor/base/helpers";


const globalImports: Imports = typeof global !== 'undefined' ? global as any : window as any;

const SF = globalImports.SF || (globalImports.SF = {});
SF.Util = SF.Util || {} as IEditorUtil;

class Renderer implements IEditorRenderer {
    public _board: IEditorCanvas;
    public _inProcess: boolean;
    public outputFormat:string = '';
    public keepBleedInPreview: boolean = true;
    public readonly _queue: any[];
    public boundOnReady!: () => void;

    constructor(options?:{fontService: any}) {
        this._board = this.createBoard() as any;
        this._board.threeDpreview = true;
        if (options && options.fontService) {
            this._board.fontService = options.fontService;
        }
        this._inProcess = false;
        this._queue = [];
    }

    public createBoard(): IStorefrontStaticCanvas {
        return new globalImports.SF.Editor.StaticCanvas();
    }

    public createCanvas(width: number, height: number): HTMLCanvasElement {
        const canvas = fabric.util.createCanvasElement();
        canvas.width = width;
        canvas.height = height;

        return canvas;
    }

    public scaleBoard(width: number, height: number, scale: number): void {
        this._board._setBackstoreDimension('width', width * scale);
        this._board._setBackstoreDimension('height', height * scale);

        // set scale
        if (this._board.viewportTransform) {
            this._board.viewportTransform[0] = scale;
            this._board.viewportTransform[3] = scale;
        } else {
            console.warn('viewportTransform is not set');
        }

    }

    public drawImage(context: CanvasRenderingContext2D, sourceX: number, width: number, height: number, sourceY: number = 0): void {

        context.drawImage(this._board.getElement(), sourceX, sourceY, width, height, 0, 0, width, height);
    }

    /**
     *
     * @param edits
     * @param showDefaultText
     */
    public setBoard(edits: IEditorElementCollection, showDefaultText: boolean): Promise<void> {

        globalImports.SF.Util.Translator.boardSetter(this._board, edits, !showDefaultText);
        return Promise.resolve();

    }

    // setup ready listener
    public onReady(edits: IEditorElementCollection, scale: number, callback?: (snapshots: HTMLCanvasElement[], queue: any) => void): void {
        // we need a snapshot per page
        const snapshots:HTMLCanvasElement[] = [];
        for (let i = 0; i < edits.pages.length; i++) {

            const page = edits.pages[i];
            if (page.type !== globalImports.SF.Model.PageTypes.SPINE || page.width !== 0) {
                // init vars (note: we floor dimensions and floor the offset, because if it goes over, firefox cries)
                let pageWidth: number = Math.floor(page.width * scale);
                let pageHeight: number = Math.floor(page.height * scale);
                let pageOffset: number = Math.round(globalImports.SF.Util.Translator.calculateOffset(edits, i) * scale);
                let pageOffsetX: number = 0;
                let pageOffsetY: number = 0;
                if (this.isCoverPageWithBleed(page)) {
                    const bleedValues = this.getCoverPageBleedValues(scale);
                    if (page.type === SF.Model.PageTypes.BACK_COVER) {
                        pageWidth -= bleedValues.left;
                        pageOffset = bleedValues.left;
                    } else if (page.type === SF.Model.PageTypes.FRONT_COVER) {
                        pageWidth -= bleedValues.right;
                    }
                    pageHeight -= (bleedValues.top + bleedValues.bottom);
                } else if (this.shouldRemoveWrapFromPreview(page)) {
                    const bleedValues = this.getPageBleedValues(page, scale);
                    pageWidth -= (bleedValues.left + bleedValues.right);
                    pageHeight -= (bleedValues.top + bleedValues.bottom);
                    pageOffsetX = bleedValues.left;
                    pageOffsetY = bleedValues.top;
                }
                // create a canvas for each snapshot
                const canvas = this.createCanvas(pageWidth, pageHeight);

                // prepare context
                const context = canvas.getContext('2d');

                if (context) {

                    if (this.outputFormat !== 'png' && this.outputFormat !== 'txt' && this.outputFormat !== 'webp') {
                        // make all renders have a white background, and not transparent as long as it is not a png or text output format.
                        context.fillStyle = '#ffffff';
                        context.fillRect(0, 0, pageWidth, pageHeight);
                    }
                    if (this.isCoverPageWithBleed(page)) {
                        const bleedValues = this.getCoverPageBleedValues(scale);
                        // we need to take a portion of the board and draw it into the canvas
                        this.drawImage.call(this, context, pageOffset, pageWidth, pageHeight, bleedValues.top);
                    } else if (this.shouldRemoveWrapFromPreview(page)) {
                        this.drawImage.call(this, context, pageOffsetX, pageWidth, pageHeight, pageOffsetY);
                    } else {
                        // we need to take a portion of the board and draw it into the canvas
                        this.drawImage.call(this, context, pageOffset, pageWidth, pageHeight);
                    }

                    // add each canvas
                    snapshots.push(canvas);
                } else {
                    console.warn('Unable to get context from canvas');
                }
            }
        }

        // execute the callback and return the canvases
        if (callback) {
            callback(snapshots, this._queue);
        }

        // stop listening for ready
        this._board.off('ready', this.boundOnReady);

        // process complete
        this._inProcess = false;

        // if there was a queue, start processing it
        if (this._queue.length > 0) {
            this.render.apply(this, this._queue.shift());
        } else {
            this.onReadyComplete();
        }
    }

    // TODO: Override me if you need to do something when ready is complete (photosite, for example overrides this to draw the keyhole).
    protected onReadyComplete(): void {
        // Do nothing by default...
    }

    public isCoverPageWithBleed(page: IEditorPage): boolean {
        return (this._board.coverPageBleed
            && (page.type === SF.Model.PageTypes.FRONT_COVER || page.type === SF.Model.PageTypes.BACK_COVER || page.type === SF.Model.PageTypes.SPINE));
    }

    public getCoverPageBleedValues(scale: number): {top: number, bottom: number, left: number, right: number} {
        const top = Math.floor(this._board.coverPageBleed[SF.Model.CoverPhotoMarginAttribute.COVER_PHOTO_MARGIN_TOP] * scale);
        const bottom = Math.floor(this._board.coverPageBleed[SF.Model.CoverPhotoMarginAttribute.COVER_PHOTO_MARGIN_BOTTOM] * scale);
        const left = Math.floor(this._board.coverPageBleed[SF.Model.CoverPhotoMarginAttribute.COVER_PHOTO_MARGIN_LEFT] * scale);
        const right = Math.floor(this._board.coverPageBleed[SF.Model.CoverPhotoMarginAttribute.COVER_PHOTO_MARGIN_RIGHT] * scale);
        return {top: top, bottom: bottom, left: left, right: right};
    }

    public getPageBleedValues(page: IEditorPage, scale: number): {top: number, bottom: number, left: number, right: number} {
        const ProductAttribute = globalImports.SF.Model.EditorBleedProperties;
        const top = Math.floor((page.bleed[ProductAttribute.BLEED_SIDE_TOP] + page.bleed[ProductAttribute.BLEED_BACK_TOP]) * scale);
        const bottom = Math.floor((page.bleed[ProductAttribute.BLEED_SIDE_BOTTOM] + page.bleed[ProductAttribute.BLEED_BACK_BOTTOM]) * scale);
        const left = Math.floor((page.bleed[ProductAttribute.BLEED_SIDE_LEFT] + page.bleed[ProductAttribute.BLEED_BACK_LEFT]) * scale);
        const right = Math.floor((page.bleed[ProductAttribute.BLEED_SIDE_RIGHT] + page.bleed[ProductAttribute.BLEED_BACK_RIGHT]) * scale);
        return {top: top, bottom: bottom, left: left, right: right};
    }

    public shouldRemoveWrapFromPreview(page: IEditorPage): boolean {
        return page.bleed && page.bleed[SF.Model.WrapOptions.EDGE_STYLE] && !this.keepBleedInPreview;
    }

    /**
     * @param edits {SF.Model.ElementCollection}
     * @param size {number}
     * @param callback {Function}
     * @param threeDPreview {boolean}
     * @param showDefaultText
     */
    public render(edits: IEditorElementCollection, size: number,
                  callback: (snapshots: HTMLCanvasElement[], selfQueue: object[]) => void,
                  threeDPreview?: boolean, showDefaultText: boolean = false): void {
        if (this._inProcess) {
            this._queue.push(arguments);
            return;
        }

        // mark as in process
        this._inProcess = true;
        const hasSpine = edits.visiblePages.some((page: IEditorPage): boolean => {
            return page.type == SF.Model.PageTypes.SPINE;
        });
        let numberOfVisiblePages;
        let spineWidth = 0;
        if (threeDPreview) {
            numberOfVisiblePages = hasSpine ? 2 : edits.visiblePages.length;
            spineWidth = hasSpine ? edits.visiblePages[1].width : 0;
        } else {
            numberOfVisiblePages = edits.visiblePages.length;
        }

        const scale = size ? EditorHelpers.zoomToFit(edits.width - spineWidth, edits.height, size * numberOfVisiblePages, size) : 1;

        // edits.width only represents the width of the visible pages. thus we calculate total width of the edits we want to render
        const totalWidth = edits.pages.reduce((accum: number, page: IEditorPage): number => {
            return accum + page.width;
        }, 0);

        // properly size board
        this.scaleBoard(totalWidth, edits.height, scale);

        // clear and load shapes
        // setBoard can return a promise, if it doesn't create an instantly resolved promise to load the board...
        const res: Promise<void> = this.setBoard(edits, showDefaultText);
        const board = this._board;

        res.then((): void => {
            // start rendering
            board.renderAll();

            const onReady = (): void => {
                // reset pattern images to force reload when creating canvas snapshots
                EditorHelpers.resetEditsPatternImages(edits);
                EditorHelpers.loadPatternImages(board).then(() => {
                    this.onReady(edits, scale, callback);
                }).catch((err: string) => {
                    console.error(err);
                });
            };

            // capture snapshot when ready
            if (board.objectsToLoad === 0) {
                onReady();
            } else {
                this.boundOnReady = onReady.bind(this);
                board.on('ready', this.boundOnReady);
            }
        }).catch((err: string) => {
            console.error(err);
        });
    }
}

SF.Util.Renderer = Renderer;